import { AbstractControl, FormBuilder } from "@angular/forms";
import { Type, ChangeDetectorRef, ComponentFactoryResolver, Injector, ViewRef } from "@angular/core";

import { bufferCount, map, filter } from "rxjs/operators";
import { FormContext } from "../form/form-context";
import { FormFieldSetting } from "../form-settings/form-field-setting";
import { FormRootDirective } from "../form/form-root.directive";
import { DynamicController } from "./dynamic-controller";
import { BasicFormFieldValue } from "../form/form-model";
import {
  FIELD_FORM_CONTROL,
  FIELD_METADATA,
  FormFieldInjector,
  FIELD_DYNAMIC_CONTROLLER,
  FIELD_NAME
} from "./form-field-injector";
import { FormFieldSettings } from "../form-settings/form-field-settings";
import { SubType } from "../form-settings/sub-type";
import { DynamicFormType } from "./form-field-type";

export interface CreateFormContextOptions<M, T extends AbstractControl, S extends FormFieldSetting<M>> {
  initialValue?: BasicFormFieldValue;
  formControl?: T;
  setting: S;
  settings: FormFieldSettings;
}

export interface FieldFactory<T extends AbstractControl, S extends FormFieldSetting<any>> {
  create<M>(params: CreateFormContextOptions<M, T, S>): FormContext<M, T>;
  render(formRoot: FormRootDirective, root: FormContext<any, T>): ViewRef;
  updateChildren(context: FormContext<any, T>): void;
}

export class ParentFieldFactory implements FieldFactory<AbstractControl, FormFieldSetting<any>> {
  constructor(
    private factories: { [key: string]: Type<FieldFactory<AbstractControl, FormFieldSetting<any>>> },
    private componentFactoryResolver: ComponentFactoryResolver,
    private injector: Injector,
    private formBuilder: FormBuilder,
    private settings: FormFieldSettings
  ) {}

  public create<M>(
    params: CreateFormContextOptions<M, AbstractControl, FormFieldSetting<M>>
  ): FormContext<M, AbstractControl> {
    return this.getFactory(params.setting).create(params);
  }

  public render(formRoot: FormRootDirective, root: FormContext<any, AbstractControl>): ViewRef {
    return this.getFactory(root.setting).render(formRoot, root);
  }

  public updateChildren(context: FormContext<any, AbstractControl>): void {
    return this.getFactory(context.setting).updateChildren(context);
  }

  public resolveComponent<M, H extends AbstractControl = AbstractControl>(
    formControl: H,
    setting: FormFieldSetting<M>,
    value: BasicFormFieldValue,
    dynamicController: DynamicController
  ): FormContext<M, H> {
    let componentFactory;

    try {
      componentFactory = this.componentFactoryResolver.resolveComponentFactory(setting.component);
    } catch (e) {
      console.error(
        `Cannot resolve component factory for ${setting.name}. ${setting.component ? "" : "Component is undefined."}`
      );
      throw e;
    }

    const additionalTokens = new WeakMap();
    additionalTokens.set(FIELD_METADATA, setting.metadata || {});
    additionalTokens.set(FIELD_FORM_CONTROL, formControl);
    additionalTokens.set(FIELD_DYNAMIC_CONTROLLER, dynamicController);
    additionalTokens.set(FIELD_NAME, setting.name);

    const dynamicInjector = new FormFieldInjector(this.injector, additionalTokens);
    const componentRef = componentFactory.create(dynamicInjector);

    dynamicController.componentRef = componentRef;

    this.patchAsyncChangeDetection(formControl, componentRef.changeDetectorRef);

    return {
      initialValue: value,
      formControl,
      setting,
      settings: this.settings,
      componentRef,
      children: []
    } as FormContext<M, H>;
  }

  private patchAsyncChangeDetection(formControl: AbstractControl, cdr: ChangeDetectorRef) {
    formControl.statusChanges
      .pipe(
        bufferCount(2, 1),
        map(([prevState]) => prevState),
        filter(prevState => prevState === "PENDING")
      )
      .subscribe(() => cdr.markForCheck());
  }

  private getFactory<M>(setting: FormFieldSetting<M>): FieldFactory<AbstractControl, FormFieldSetting> {
    const factoryType = SubType.toSubType(setting.type).type;
    const factory = this.factories[factoryType];

    if (!factory) {
      throw new Error(`Field factory ${factoryType} does not exist.`);
    }

    return new factory(this, this.formBuilder);
  }
}
